import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const LoadingContext = createContext();
const SHOW_DELAY_MS = 200;
const MIN_VISIBLE_MS = 300;

export default function LoadingContextProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [subscribers, setSubscribers] = useState({});
  const nextId = useRef(0);
  const anonymousIds = useRef([]);
  const showTimer = useRef(null);
  const hideTimer = useRef(null);
  const shownAt = useRef(0);

  const startLoading = useCallback((id) => {
    const loadingId = id || `loading-${nextId.current++}`;
    if (!id) anonymousIds.current.push(loadingId);

    setSubscribers((curr) => {
      const newCurr = { ...curr };
      newCurr[loadingId] = true;
      return newCurr;
    });

    return loadingId;
  }, []);

  const stopLoading = useCallback((id) => {
    const loadingId = id || anonymousIds.current.pop();
    if (!loadingId) return;

    setSubscribers((curr) => {
      const newCurr = { ...curr };
      delete newCurr[loadingId];
      return newCurr;
    });
  }, []);

  const resetLoading = useCallback(() => {
    anonymousIds.current = [];
    shownAt.current = 0;
    clearTimeout(showTimer.current);
    clearTimeout(hideTimer.current);
    setSubscribers({});
    setLoading(false);
  }, []);

  useEffect(() => {
    const hasSubscribers = Object.keys(subscribers).length !== 0;

    if (hasSubscribers) {
      clearTimeout(hideTimer.current);

      if (!loading && !showTimer.current) {
        showTimer.current = setTimeout(() => {
          shownAt.current = Date.now();
          setLoading(true);
          showTimer.current = null;
        }, SHOW_DELAY_MS);
      }

      return;
    }

    clearTimeout(showTimer.current);
    showTimer.current = null;

    if (loading) {
      const visibleFor = Date.now() - shownAt.current;
      const hideDelay = Math.max(MIN_VISIBLE_MS - visibleFor, 0);

      hideTimer.current = setTimeout(() => {
        shownAt.current = 0;
        setLoading(false);
        hideTimer.current = null;
      }, hideDelay);
    }
  }, [loading, subscribers]);

  useEffect(() => {
    return () => {
      clearTimeout(showTimer.current);
      clearTimeout(hideTimer.current);
    };
  }, []);

  return (
    <LoadingContext.Provider
      value={{ loading, startLoading, stopLoading, resetLoading }}
    >
      {children}
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  return useContext(LoadingContext);
}
